/**
 * Seal (scalloped disc) behind the Centellian24 extra-credit badge.
 *
 * Path data is the verbatim Figma export of the seal in `5775:81038`. Do not
 * redraw it by hand — the three badge variants share this one artwork and
 * differ only in rendered size.
 */
export const CREDIT_SEAL_STAR_VIEWBOX_SIZE = 100.884;

/**
 * Campaign fill. Figma uses a raw #1A8166 that no design-system token carries
 * (`success.700` is #15803D and `success.800` is #016730), so it lives here as
 * an explicit campaign constant pending a token decision from design.
 */
export const CENTELLIAN24_SEAL_FILL = '#1A8166';

const STAR_PATH_D =
  'M49.8189 1.21828C50.1835 0.927617 50.7003 0.927617 51.065 1.21828L57.7661 6.56203C58.5262 7.16817 59.5331 7.36827 60.4673 7.09914L68.7027 4.72609C69.1509 4.59694 69.6293 4.79534 69.855 5.20363L74.0005 12.7046C74.4708 13.5554 75.3245 14.126 76.2905 14.2349L84.8071 15.1948C85.2708 15.2471 85.6367 15.6131 85.689 16.0767L86.6489 24.5933C86.7578 25.5593 87.3284 26.4131 88.1792 26.8833L95.6802 31.0288C96.0885 31.2545 96.2869 31.7329 96.1577 32.1812L93.7847 40.4165C93.5156 41.3507 93.7156 42.3576 94.3218 43.1177L99.6655 49.8189C99.9562 50.1835 99.9562 50.7003 99.6655 51.065L94.3218 57.7661C93.7156 58.5262 93.5156 59.5331 93.7847 60.4673L96.1577 68.7027C96.2869 69.1509 96.0885 69.6293 95.6802 69.855L88.1792 74.0005C87.3284 74.4708 86.7578 75.3245 86.6489 76.2905L85.689 84.8071C85.6367 85.2708 85.2708 85.6367 84.8071 85.689L76.2905 86.6489C75.3245 86.7578 74.4708 87.3284 74.0005 88.1792L69.855 95.6802C69.6293 96.0885 69.1509 96.2869 68.7027 96.1577L60.4673 93.7847C59.5331 93.5156 58.5262 93.7156 57.7661 94.3218L51.065 99.6655C50.7003 99.9562 50.1835 99.9562 49.8189 99.6655L43.1177 94.3218C42.3576 93.7156 41.3507 93.5156 40.4165 93.7847L32.1812 96.1577C31.7329 96.2869 31.2545 96.0885 31.0288 95.6802L26.8833 88.1792C26.4131 87.3284 25.5593 86.7578 24.5933 86.6489L16.0767 85.689C15.6131 85.6367 15.2471 85.2708 15.1948 84.8071L14.2349 76.2905C14.126 75.3245 13.5554 74.4708 12.7046 74.0005L5.20363 69.855C4.79534 69.6293 4.59694 69.1509 4.72609 68.7027L7.09914 60.4673C7.36827 59.5331 7.16817 58.5262 6.56203 57.7661L1.21828 51.065C0.927617 50.7003 0.927617 50.1835 1.21828 49.8189L6.56203 43.1177C7.16817 42.3576 7.36827 41.3507 7.09914 40.4165L4.72609 32.1812C4.59694 31.7329 4.79534 31.2545 5.20363 31.0288L12.7046 26.8833C13.5554 26.4131 14.126 25.5593 14.2349 24.5933L15.1948 16.0767C15.2471 15.6131 15.6131 15.2471 16.0767 15.1948L24.5933 14.2349C25.5593 14.126 26.4131 13.5554 26.8833 12.7046L31.0288 5.20363C31.2545 4.79535 31.7329 4.59694 32.1812 4.72609L40.4165 7.09914C41.3507 7.36827 42.3576 7.16817 43.1177 6.56203L49.8189 1.21828Z';

type CreditSealStarProps = {
  /** Rim thickness in viewBox units, so it survives the parent's scaling. */
  strokeWidth: number;
  /** The seal fills its positioned parent; size it there. */
  className?: string;
};

export const CreditSealStar = ({ strokeWidth, className }: CreditSealStarProps) => (
  <svg
    className={className}
    viewBox={`0 0 ${CREDIT_SEAL_STAR_VIEWBOX_SIZE} ${CREDIT_SEAL_STAR_VIEWBOX_SIZE}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d={STAR_PATH_D}
      fill={CENTELLIAN24_SEAL_FILL}
      stroke="var(--color-neutral-50)"
      strokeWidth={strokeWidth}
    />
  </svg>
);
